import React from 'react';
import {
  AutoClassificationActionContainer,
  ActionButtonsContainer,
  AutoClassificationText,
  ClassifySpendContainer,
} from './Data.styles';
import {
  IAutoClassificationFileStatus,
  IFileData,
  IFileDataType,
  IFileStatus,
} from 'utils/types';
import { LinksToSpendFileUploads } from './LinksToSpendFileUploads';
import { LinksToSpendAllocation } from './LinksToSpendAllocation';
import { ApButtonMain } from '@alixpartners/ui-components';
import { PERMISSIONS } from 'utils/constants';
import { getSelectedFiles, getFileTypeStatus } from '../settings.utils';
import { checkApplicationAllocation } from 'utils/api';
import { RouteComponentProps } from 'react-router';

interface IApplicationAllocations {
  isExistAllocationsForPersonnel: boolean;
  isExistAllocationsForThirdParty: boolean;
}

const LINK_TO_THIRD_PARTY_ALLOCATION = '/productallocation/thirdParty';
const LINK_TO_PERSONNEL_ALLOCATION = '/productallocation/personnel';

export const FilesActions = ({
  files,
  fileDataType,
  isClassificationInProgress,
  goToClassification,
  history,
  permissions,
  startAutoClassification,
}: {
  files: IFileData[];
  fileDataType: IFileDataType;
  isClassificationInProgress: boolean;
  goToClassification: () => void;
  history: RouteComponentProps['history'];
  permissions: string[];
  startAutoClassification: () => void;
}) => {
  const [applicationAllocations, setApplicationAllocation] =
    React.useState<IApplicationAllocations>({
      isExistAllocationsForPersonnel: false,
      isExistAllocationsForThirdParty: false,
    });
  const { isExistAllocationsForPersonnel, isExistAllocationsForThirdParty } =
    applicationAllocations;

  React.useEffect(() => {
    if (fileDataType === IFileDataType.PRODUCTS) {
      checkApplicationAllocation().then((resp) => {
        setApplicationAllocation(resp);
      });
    }
  }, [fileDataType]);

  const selectedFiles = getSelectedFiles(files, fileDataType);
  const revenueFiles = files.filter(
    (f: IFileData) => f.fileDataType === IFileDataType.REVENUE
  );
  const thirdPartyFiles: IFileData[] = files.filter(
    (f: IFileData) => f.fileDataType === IFileDataType.THIRD_PARTY_SPEND
  );
  const personnelFiles: IFileData[] = files.filter(
    (f: IFileData) => f.fileDataType === IFileDataType.PERSONNEL_SPEND
  );
  const applicationFiles = files.filter(
    (f: IFileData) => f.fileDataType === IFileDataType.APPLICATIONS
  );
  const productFiles = files.filter(
    (f: IFileData) => f.fileDataType === IFileDataType.PRODUCTS
  );

  const applicationFilesStatus = getFileTypeStatus(applicationFiles);
  const isSpendFiles =
    fileDataType === IFileDataType.PERSONNEL_SPEND ||
    fileDataType === IFileDataType.THIRD_PARTY_SPEND;
  const canClassifyData = permissions.includes(
    PERMISSIONS.UNPUBLISHED_DATA_READ
  );
  const canAllocatePersonnel = permissions.includes(
    PERMISSIONS.APPLICATIONS_DATA_ALLOCATE
  );
  const canAllocateProductPersonnel = permissions.includes(
    PERMISSIONS.PRODUCTS_DATA_ALLOCATE
  );
  const isAnyUnPublished = selectedFiles.some(
    (file) => file.fileState === IFileStatus.UPLOADED
  );
  const isAnyThirdPartyPublished = thirdPartyFiles.some(
    (file) => file.fileState === IFileStatus.PUBLISHED
  );
  const isAnyThirdPartyUnPublished = thirdPartyFiles.some(
    (file) =>
      file.fileState === IFileStatus.UPLOADED &&
      file.autoClassificationStatus !==
        IAutoClassificationFileStatus.IN_PROGRESS
  );
  const isAnyPersonnelPublished = personnelFiles.some(
    (file) => file.fileState === IFileStatus.PUBLISHED
  );
  const applicationPublished = applicationFilesStatus === IFileStatus.PUBLISHED;
  const isClassificationEnabled =
    canClassifyData && isSpendFiles && selectedFiles.length !== 0;
  return (
    <React.Fragment>
      {/* REVENUE actions */}
      {fileDataType === IFileDataType.REVENUE &&
      revenueFiles.length !== 0 &&
      thirdPartyFiles.length === 0 &&
      personnelFiles.length === 0 ? (
        <LinksToSpendFileUploads />
      ) : null}

      {fileDataType !== IFileDataType.THIRD_PARTY_SPEND &&
      isClassificationEnabled ? (
        <ClassifySpendContainer>
          <ApButtonMain
            iconName="dynamic_feed"
            disabled={!isAnyUnPublished}
            onClick={goToClassification}
          >
            Classify and Publish spend
          </ApButtonMain>
        </ClassifySpendContainer>
      ) : null}

      {/* THIRD PARTY actions */}
      {fileDataType === IFileDataType.THIRD_PARTY_SPEND &&
      isClassificationEnabled ? (
        <AutoClassificationActionContainer>
          <AutoClassificationText>
            Auto classification runs only for empty rows of all unpublished
            files
          </AutoClassificationText>
          <ActionButtonsContainer>
            {permissions.includes(PERMISSIONS.RUN_AUTOCLASSIFICATION) ? (
              <ApButtonMain
                iconName="grading"
                disabled={
                  isClassificationInProgress && !isAnyThirdPartyUnPublished
                }
                onClick={startAutoClassification}
              >
                Run Auto Classification
              </ApButtonMain>
            ) : null}
            <ApButtonMain
              iconName="cloud_upload_outlined"
              disabled={
                isClassificationInProgress && !isAnyThirdPartyUnPublished
              }
              onClick={goToClassification}
            >
              Manual classification / Review
            </ApButtonMain>
          </ActionButtonsContainer>
        </AutoClassificationActionContainer>
      ) : null}

      {/* APPLICATIONS actions */}
      {fileDataType === IFileDataType.APPLICATIONS && applicationPublished && (
        <LinksToSpendAllocation
          isThirdParty={isAnyThirdPartyPublished}
          isPersonnel={isAnyPersonnelPublished}
          canAllocatePersonnel={canAllocatePersonnel}
        />
      )}

      {/* PRODUCTS actions */}
      {fileDataType === IFileDataType.PRODUCTS ? (
        <AutoClassificationActionContainer>
          <AutoClassificationText>
            Please make sure application data is uploaded before you begin
            allocating product spend
          </AutoClassificationText>
          <ActionButtonsContainer>
            <ApButtonMain
              disabled={
                productFiles.length === 0 ||
                isClassificationInProgress ||
                !isExistAllocationsForThirdParty
              }
              onClick={() => {
                history.push(LINK_TO_THIRD_PARTY_ALLOCATION);
              }}
            >
              Third party spend Allocation
            </ApButtonMain>
            {canAllocateProductPersonnel && (
              <ApButtonMain
                disabled={
                  productFiles.length === 0 ||
                  isClassificationInProgress ||
                  !isExistAllocationsForPersonnel
                }
                onClick={() => {
                  history.push(LINK_TO_PERSONNEL_ALLOCATION);
                }}
              >
                Personnel spend allocation
              </ApButtonMain>
            )}
          </ActionButtonsContainer>
        </AutoClassificationActionContainer>
      ) : null}
    </React.Fragment>
  );
};
